import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowLeft, Shield, Lock, AlertTriangle, Mail } from "lucide-react";

/**
 * Public Privacy Policy for AXIORA.
 * Mounted at BOTH `/privacy-policy` (canonical) and `/privacy` (legacy alias)
 * as public routes — no auth, no app shell. Mobile + desktop responsive with a
 * sticky table-of-contents on large screens.
 */
const EFFECTIVE_DATE = "1 June 2026";
const LAST_UPDATED = "9 June 2026";

const SECTIONS = [
  { id: "introduction", title: "1. Introduction & scope" },
  { id: "independence", title: "2. Independence & no government authorization" },
  { id: "information-collected", title: "3. Information we collect" },
  { id: "taxpayer-data", title: "4. Taxpayer & tax information" },
  { id: "purpose", title: "5. Purpose of processing" },
  { id: "consent", title: "6. Your consent & authorization" },
  { id: "integrations", title: "7. Third-party integrations" },
  { id: "storage-retention", title: "8. Data storage & retention" },
  { id: "security", title: "9. Data security" },
  { id: "sharing", title: "10. Data sharing & disclosure" },
  { id: "rights", title: "11. Your rights" },
  { id: "deletion", title: "12. Account & data deletion" },
  { id: "cookies", title: "13. Cookies & logs" },
  { id: "children", title: "14. Children" },
  { id: "changes", title: "15. Changes to this policy" },
  { id: "law", title: "16. Governing law" },
  { id: "grievance", title: "17. Grievance officer & contact" },
];

function H2({ id, children }) {
  return (
    <h2 id={id} className="scroll-mt-24 text-xl font-semibold tracking-tight text-gray-900 mt-10 first:mt-0">
      {children}
    </h2>
  );
}

const P = ({ children }) => <p className="mt-3 text-[15px] leading-relaxed text-gray-700">{children}</p>;
const UL = ({ children }) => <ul className="mt-3 space-y-2 text-[15px] leading-relaxed text-gray-700 list-disc pl-5 marker:text-blue-600">{children}</ul>;

export default function PrivacyPolicy() {
  useEffect(() => {
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "smooth";
    return () => { document.documentElement.style.scrollBehavior = prev; };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 text-gray-900 hover:text-blue-700 transition-colors">
            <span className="w-7 h-7 rounded-md bg-blue-700 text-white grid place-items-center"><Calculator className="w-4 h-4" /></span>
            <span className="font-semibold tracking-tight">AXIORA</span>
          </Link>
          <div className="flex-1" />
          <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1.5 transition-colors" data-testid="link-back-signin">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="inline-flex items-center gap-2 text-blue-700">
            <Shield className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-[0.15em] font-semibold">Legal · Privacy</span>
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-gray-900">Privacy Policy</h1>
          <p className="mt-3 max-w-2xl text-base text-gray-600 leading-relaxed">
            How AXIORA collects, uses, protects and shares your information — including sensitive
            financial and tax data that we process <span className="font-medium text-gray-800">only with your express authorization</span>.
          </p>
          <div className="mt-5 flex flex-wrap gap-2.5 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-gray-700" data-testid="privacy-effective-date">
              <span className="font-medium">Effective date:</span> {EFFECTIVE_DATE}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-gray-700" data-testid="privacy-last-updated">
              <span className="font-medium">Last updated:</span> {LAST_UPDATED}
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:grid lg:grid-cols-[240px_1fr] lg:gap-12" data-testid="privacy-policy">
        {/* TOC */}
        <nav className="hidden lg:block" data-testid="privacy-toc" aria-label="On this page">
          <div className="sticky top-24">
            <p className="text-[11px] uppercase tracking-[0.15em] font-semibold text-gray-400 mb-3">On this page</p>
            <ul className="space-y-1.5 border-l border-gray-200">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="block -ml-px border-l border-transparent pl-3 py-0.5 text-sm text-gray-500 hover:text-blue-700 hover:border-blue-600 transition-colors">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Content */}
        <article className="min-w-0">
          <div className="rounded-lg border border-gray-200 bg-white p-6 sm:p-8 shadow-sm">
            <H2 id="introduction">1. Introduction &amp; scope</H2>
            <P>
              This Privacy Policy (&quot;Policy&quot;) explains how <strong>AXIORA</strong> (&quot;AXIORA&quot;, &quot;we&quot;,
              &quot;us&quot; or &quot;our&quot;) collects, uses, discloses, stores and protects information when you access
              or use our web application, mobile applications and related services (collectively, the &quot;Service&quot;).
              AXIORA is a cloud-based accounting, bookkeeping and income-tax preparation software intended for
              businesses, professionals and individual taxpayers in India.
            </P>
            <P>
              This Policy should be read together with our Terms of Service. By creating an account or using the
              Service, you acknowledge that you have read and understood this Policy. Where the law requires your
              consent for a specific processing activity (for example, connecting an income-tax data integration),
              we obtain that consent separately and expressly, as described in Section 6.
            </P>

            <H2 id="independence">2. Independence &amp; no government authorization</H2>
            <div className="mt-3 flex gap-3 rounded-md border border-amber-300 bg-amber-50 p-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-[15px] leading-relaxed text-amber-900">
                AXIORA is <strong>independent</strong> accounting and tax-preparation software. AXIORA is
                <strong> not affiliated with, endorsed by, or authorized by</strong> the Income Tax Department of
                India, the Central Board of Direct Taxes (CBDT), the Goods and Services Tax Network (GSTN), the
                Protean/NSDL/TIN systems, or any other government body or authority. AXIORA is not itself an
                Electronic Return Intermediary (ERI).
              </div>
            </div>
            <P>
              Any retrieval of data from, or filing/payment activity on, a government portal is <strong>never</strong>
              performed by AXIORA directly and <strong>never</strong> uses your government-portal password. Such
              activities are facilitated only through independent, authorized third-party intermediaries (such as
              Quicko — see Section 7) and only after you expressly authorize the specific integration and complete
              that provider&apos;s own secure consent flow.
            </P>

            <H2 id="information-collected">3. Information we collect</H2>
            <P>We collect only the information needed to provide the Service:</P>
            <UL>
              <li><strong>Account &amp; identity data</strong> — your name, email address, password (stored only as a bcrypt hash), phone number, and business details such as legal name, GSTIN, PAN and registered address that you enter during registration and company setup.</li>
              <li><strong>Business &amp; accounting data</strong> — vouchers, ledgers, invoices, bills, stock/inventory movements, bank-statement uploads, payroll records and reports that you create or upload. This data belongs to you; AXIORA acts as a custodian/processor on your behalf.</li>
              <li><strong>Taxpayer &amp; tax information</strong> — described separately in Section 4, processed only with your express authorization.</li>
              <li><strong>Payment data</strong> — for subscription billing, limited data (billing email, phone, amount) handled by our payment processor. AXIORA does not store your full card details.</li>
              <li><strong>Device, log &amp; usage data</strong> — IP address, browser/device type, timestamps, pages viewed and actions taken, retained for security, audit and troubleshooting.</li>
              <li><strong>Camera / microphone (mobile)</strong> — used only when you explicitly scan a document/receipt or use voice-to-text; processed transiently for that action.</li>
              <li><strong>Biometric unlock (mobile)</strong> — Face ID / fingerprint is handled entirely by your device&apos;s operating system. We only receive a pass/fail result and never see or store your biometric template.</li>
            </UL>

            <H2 id="taxpayer-data">4. Taxpayer &amp; tax information</H2>
            <P>
              When, and only when, you expressly enable a relevant tax feature or integration, AXIORA may process
              sensitive tax and financial information, which may include:
            </P>
            <UL>
              <li>Permanent Account Number (PAN), name, date of birth, taxpayer category and residential status;</li>
              <li>Contact and address details associated with the taxpayer;</li>
              <li>Income information across heads (salary, house property, capital gains, business/profession, other sources);</li>
              <li>Tax Deducted at Source (TDS) / Tax Collected at Source (TCS) details;</li>
              <li>Annual Information Statement (AIS), Taxpayer Information Summary (TIS) and Form 26AS information;</li>
              <li>Advance tax / self-assessment tax payments and challan details;</li>
              <li>Income-tax return (ITR) metadata such as assessment year, form type, filing status, acknowledgement number and e-verification status; and</li>
              <li>Tax computation figures, deductions and other financial/tax information you enter, upload or authorize us to retrieve.</li>
            </UL>
            <P>
              This information is used solely to help you prepare, review, compute and organize your tax position
              within the Service. We do not use your tax information for advertising and we do not sell it. Where you
              authorize a third-party integration to retrieve such data (for example, your taxpayer profile, ITR
              history or tax computation via Quicko), the retrieval is read-only and governed by your consent and by
              that provider&apos;s own privacy terms.
            </P>

            <H2 id="purpose">5. Purpose of processing</H2>
            <UL>
              <li>To operate core features — record vouchers, maintain ledgers, generate financial statements, GST reports, payroll and income-tax computations;</li>
              <li>To create, review and organize your tax data and, where you authorize it, to fetch read-only tax information from an integration;</li>
              <li>To authenticate you and secure your account (rate-limiting, session management, audit logging);</li>
              <li>To send transactional communications — verification codes, password resets, subscription receipts and documents you choose to email;</li>
              <li>To process your subscription payment;</li>
              <li>To maintain, debug and improve the Service; and</li>
              <li>To comply with applicable law and respond to lawful requests.</li>
            </UL>
            <P><strong>We do not sell your personal data, and we do not use it for third-party advertising.</strong></P>

            <H2 id="consent">6. Your consent &amp; authorization</H2>
            <P>
              We rely on your consent (and, for your own accounting records, on the necessity of performing the
              Service you signed up for) as the lawful basis for processing. Sensitive integrations are strictly
              opt-in:
            </P>
            <UL>
              <li><strong>Express, feature-level consent</strong> — connecting a tax-data integration (such as the Income-Tax Portal via Quicko) requires a deliberate action by you. You are then taken to the provider&apos;s own secure page to authenticate and grant consent (including any OTP). AXIORA never collects or stores your income-tax portal password.</li>
              <li><strong>Scope</strong> — an integration accesses only the data categories described at the time you connect it, and only for as long as the connection remains active.</li>
              <li><strong>Withdrawal</strong> — you may disconnect an integration or withdraw consent at any time from within the Service. Withdrawal stops future processing under that consent but does not affect processing already carried out lawfully.</li>
            </UL>

            <H2 id="integrations">7. Third-party integrations &amp; sub-processors</H2>
            <P>We share data with a limited set of vetted providers strictly to deliver the Service:</P>
            <UL>
              <li><strong>Quicko (Quicko Infosoft Private Limited)</strong> — an independent, authorized intermediary used, only if you connect it, to retrieve read-only income-tax information such as your taxpayer profile, ITR history and tax computation, and to let you continue e-filing / tax payment on Quicko&apos;s own platform. Consent and OTP happen on Quicko&apos;s secure page; AXIORA does not receive your portal password. Access tokens returned to AXIORA are encrypted at rest and can be revoked by disconnecting. Quicko&apos;s handling of your data is governed by Quicko&apos;s own privacy policy.</li>
              <li><strong>Razorpay</strong> — to process AXIORA subscription payments. Razorpay receives your billing email, phone and the amount; it does not receive your accounting or tax data.</li>
              <li><strong>Resend</strong> — to deliver transactional email (verification codes, password resets, and documents you explicitly choose to email).</li>
              <li><strong>Cloud &amp; AI infrastructure</strong> — our application, database and processing run on managed cloud infrastructure. Certain optional features (for example, statement/receipt extraction) may send the specific content you submit to a large-language-model provider solely to perform that task; such content is not used to train third-party models.</li>
            </UL>

            <H2 id="storage-retention">8. Data storage &amp; retention</H2>
            <P>
              Your data is stored in managed databases and encrypted in transit (TLS) and at rest. We retain your
              accounting and tax data for as long as your account is active, plus a limited grace period after
              cancellation so you can re-activate without losing your books.
            </P>
            <P>
              Certain records must be retained to meet statutory obligations — for example, books of account under
              Section 44AA of the Income-tax Act, 1961 and Section 35 of the CGST Act, 2017, which generally require
              preservation for a number of years. Where you request deletion, we delete identifying personal data and
              retain only the minimum records required by law, in a restricted or anonymized form, for the mandated
              period.
            </P>

            <H2 id="security">9. Data security</H2>
            <UL>
              <li>Passwords are stored using bcrypt with a per-user salt; we never store them in plaintext.</li>
              <li>Sessions use httpOnly, Secure cookies; access tokens for connected integrations are encrypted at rest.</li>
              <li>Data is transmitted over encrypted TLS connections.</li>
              <li>Administrative and sensitive actions are recorded in an audit trail.</li>
              <li>Access is restricted on a need-to-know basis.</li>
            </UL>
            <P>
              No method of transmission or storage is perfectly secure. If we become aware of a personal-data breach
              that affects you, we will notify you and the relevant authority as required by applicable law and
              without undue delay.
            </P>

            <H2 id="sharing">10. Data sharing &amp; disclosure</H2>
            <P>
              We do not sell or rent your personal data. We disclose data only: (a) to the sub-processors listed in
              Section 7 to provide the Service; (b) with your direction or consent (for example, when you email a
              document or connect an integration); (c) to comply with a valid legal obligation, court order or lawful
              government request; (d) to establish, exercise or defend legal claims; or (e) in connection with a
              merger, acquisition or asset transfer, subject to this Policy.
            </P>

            <H2 id="rights">11. Your rights</H2>
            <P>Subject to applicable law, including the Digital Personal Data Protection Act, 2023, you may:</P>
            <UL>
              <li>Access and obtain a copy of your personal data;</li>
              <li>Correct or complete inaccurate or incomplete data;</li>
              <li>Erase your data (subject to the statutory retention in Section 8);</li>
              <li>Withdraw consent for non-essential or integration-based processing;</li>
              <li>Nominate another individual to exercise your rights in the event of death or incapacity; and</li>
              <li>Raise a grievance with our Grievance Officer (Section 17).</li>
            </UL>
            <P>To exercise any right, contact us using the details in Section 17. We may need to verify your identity before acting on a request.</P>

            <H2 id="deletion">12. Account &amp; data deletion</H2>
            <P>
              You can request deletion of your account and associated personal data at any time by writing to us at
              the contact address below, or by using an in-app deletion option where available. Upon a verified
              request we will: disconnect any active integrations and revoke stored tokens; delete or irreversibly
              anonymize your personal and tax data; and retain only records we are legally required to keep, for the
              minimum period mandated, after which they are deleted. Deletion is permanent and cannot be undone.
            </P>

            <H2 id="cookies">13. Cookies &amp; logs</H2>
            <P>
              We use strictly necessary cookies to keep you signed in (an httpOnly session cookie) and to protect the
              Service. We do not use third-party advertising or cross-site tracking cookies. Our servers keep standard
              logs (such as IP address, timestamps and request metadata) for security, abuse-prevention and
              diagnostics. You can clear cookies in your browser, though doing so will sign you out.
            </P>

            <H2 id="children">14. Children</H2>
            <P>
              The Service is intended for use by adults (18 years or older) and is not directed at children. We do not
              knowingly collect personal data from minors. If you believe a minor has provided us data, contact us and
              we will delete it.
            </P>

            <H2 id="changes">15. Changes to this policy</H2>
            <P>
              We may update this Policy from time to time. When we make material changes, we will update the &quot;Last
              updated&quot; date above and, where appropriate, notify you by email or an in-app notice before the changes
              take effect. Your continued use of the Service after the effective date of an update constitutes
              acceptance of the revised Policy.
            </P>

            <H2 id="law">16. Governing law</H2>
            <P>
              This Policy is governed by the laws of India. Processing of personal data is undertaken in accordance
              with applicable Indian law, including the Digital Personal Data Protection Act, 2023, the Information
              Technology Act, 2000 and rules thereunder, and — insofar as they relate to records we help you maintain —
              the Income-tax Act, 1961 and the Central Goods and Services Tax Act, 2017. Disputes are subject to the
              exclusive jurisdiction of the competent courts in India.
            </P>

            <H2 id="grievance">17. Grievance officer &amp; contact</H2>
            <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-center gap-2 text-blue-800 font-semibold text-sm"><Lock className="w-4 h-4" /> Contact us</div>
              <P>
                For any privacy question, or to exercise the rights in Section 11, email us at{" "}
                <a href="mailto:privacy@ankmarfatia.com" className="text-blue-700 font-medium hover:underline">privacy@ankmarfatia.com</a>.
              </P>
              <p className="mt-3 text-[14px] leading-relaxed text-gray-700">
                <strong>Grievance Officer</strong> (in accordance with the Digital Personal Data Protection Act, 2023
                and Rule 4 of the IT (Intermediary Guidelines) Rules, 2021):
              </p>
              <ul className="mt-2 text-[14px] leading-relaxed text-gray-700 space-y-0.5">
                <li className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-blue-600" /> Email: <a href="mailto:grievance@ankmarfatia.com" className="text-blue-700 font-medium hover:underline">grievance@ankmarfatia.com</a></li>
                <li>Response time: we aim to acknowledge within 48 hours and resolve within 30 days of receipt.</li>
              </ul>
            </div>
          </div>

          <p className="mt-6 text-xs text-gray-500">
            This Policy is provided for transparency about our data practices and does not constitute legal or tax
            advice. Please consult a qualified professional for advice specific to your situation.
          </p>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-xs text-gray-500">
          <span>© {new Date().getFullYear()} AXIORA. All rights reserved.</span>
          <div className="flex gap-4">
            <Link to="/" className="hover:text-gray-800 transition-colors">Home</Link>
            <Link to="/login" className="hover:text-gray-800 transition-colors">Sign in</Link>
            <a href="#introduction" className="hover:text-gray-800 transition-colors">Back to top</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
